import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Mail, Loader2, ArrowRight, Lock } from "lucide-react";
import { lovable } from "@/integrations/lovable/index";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isPasswordPwned } from "@/lib/hibp";

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  lang: "fr" | "en";
}

const copy = {
  fr: {
    title: "Crée ton compte pour sauvegarder",
    description: "Inscris-toi en 10 secondes pour retrouver tes favoris, voyages et recommandations.",
    google: "Continuer avec Google",
    apple: "Continuer avec Apple",
    email: "Continuer avec Email",
    or: "ou",
    later: "Plus tard",
    emailPlaceholder: "Email",
    passwordPlaceholder: "Mot de passe (6+ caractères)",
    signUp: "Créer mon compte",
    signIn: "Se connecter",
    switchToLogin: "Déjà un compte ? Se connecter",
    switchToSignup: "Pas de compte ? S'inscrire",
    checkEmail: "Vérifie ta boîte mail pour confirmer ton compte",
  },
  en: {
    title: "Create an account to save",
    description: "Sign up in 10 seconds to keep your favorites, trips and recommendations.",
    google: "Continue with Google",
    apple: "Continue with Apple",
    email: "Continue with Email",
    or: "or",
    later: "Later",
    emailPlaceholder: "Email",
    passwordPlaceholder: "Password (6+ characters)",
    signUp: "Create account",
    signIn: "Sign in",
    switchToLogin: "Already have an account? Sign in",
    switchToSignup: "No account? Sign up",
    checkEmail: "Check your email to confirm your account",
  },
};

const AuthModal = ({ open, onClose, lang }: AuthModalProps) => {
  const t = copy[lang];
  const [showEmail, setShowEmail] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState<string | null>(null);

  const handleOAuth = async (provider: "google" | "apple") => {
    setLoading(provider);
    try {
      const { error } = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: "https://travelmatedev.lovable.app",
      });
      if (error) throw error;
    } catch (e: any) {
      toast.error(e.message || "Authentication failed");
    } finally {
      setLoading(null);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading("email");
    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onClose();
      } else {
        const { pwned, count } = await isPasswordPwned(password);
        if (pwned) {
          toast.error(
            lang === "fr"
              ? `Ce mot de passe a été trouvé ${count.toLocaleString()} fois dans des fuites de données. Choisis-en un autre.`
              : `This password was found ${count.toLocaleString()} times in data breaches. Please choose another one.`
          );
          setLoading(null);
          return;
        }
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: "https://travelmatedev.lovable.app" },
        });
        if (error) throw error;
        toast.success(t.checkEmail);
      }
    } catch (e: any) {
      toast.error(e.message || "Error");
    } finally {
      setLoading(null);
    }
  };

  const resetState = () => {
    setShowEmail(false);
    setIsLogin(false);
    setEmail("");
    setPassword("");
    setLoading(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); resetState(); } }}>
      <DialogContent className="sm:max-w-sm rounded-2xl p-6">
        <DialogHeader className="text-center">
          <DialogTitle className="text-lg font-bold">{t.title}</DialogTitle>
          <DialogDescription className="text-sm">{t.description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2.5 mt-3">
          {/* Google */}
          <button
            onClick={() => handleOAuth("google")}
            disabled={!!loading}
            className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-xl border border-border bg-card text-foreground font-medium text-sm hover:bg-secondary/80 transition-colors disabled:opacity-50"
          >
            {loading === "google" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            )}
            {t.google}
          </button>

          {/* Apple */}
          <button
            onClick={() => handleOAuth("apple")}
            disabled={!!loading}
            className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-xl border border-border bg-card text-foreground font-medium text-sm hover:bg-secondary/80 transition-colors disabled:opacity-50"
          >
            {loading === "apple" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
            )}
            {t.apple}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-1">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">{t.or}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {!showEmail ? (
            <>
              <button
                onClick={() => setShowEmail(true)}
                className="flex items-center justify-center gap-3 w-full py-3 px-4 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors"
              >
                <Mail className="w-4 h-4" />
                {t.signUp}
              </button>
              <button
                onClick={() => { setShowEmail(true); setIsLogin(true); }}
                className="text-xs text-primary hover:text-primary/80 transition-colors text-center"
              >
                {t.switchToLogin}
              </button>
            </>
          ) : (
            <form onSubmit={handleEmailSubmit} className="flex flex-col gap-2.5">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  placeholder={t.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="password"
                  placeholder={t.passwordPlaceholder}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={!!loading}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {loading === "email" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    {isLogin ? t.signIn : t.signUp}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-xs text-primary hover:text-primary/80 transition-colors"
              >
                {isLogin ? t.switchToSignup : t.switchToLogin}
              </button>
            </form>
          )}

          <button
            onClick={() => { onClose(); resetState(); }}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
          >
            {t.later}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
